import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSchedulerSidebar } from '@contexts/scheduler/sidebar';
import { useSchedulerContext } from '@contexts/scheduler';
import { useDropdownsContext } from '@contexts/dropdowns';
import { snakeToCamelCase } from '@utils/textHelpers';
import { sortByName } from '@components/scheduler/sidebar/crew/helpers';
import { SchedulerSidebarCrewMember } from '@contexts/scheduler/sidebar/types';
import { getConflicts } from '../multiDay/helpers';
import { PhoneResourcesProps } from './types';

export const useCrewOptions = ({
  multi,
  specialty,
}: Pick<PhoneResourcesProps, 'multi' | 'specialty'>) => {
  const { t: tPrimaryRoles } = useTranslation('app', { keyPrefix: 'primaryRole' });
  const { crewMembers, setState, conflictsByUserId } = useSchedulerSidebar();
  const {
    active: { schedule },
  } = useSchedulerContext();
  const {
    crewMembers: { options: crewOptions, disabled: crewDisabled },
    fieldUsers: { options: fieldUserOptions, disabled: fieldUsersDisabled },
  } = useDropdownsContext();

  const multiConflicts = (id: string, name: string) => {
    if (!multi) return {};
    const hits = getConflicts(multi.lookup, 'users', id);
    return {
      conflictDays: new Set(hits.map((hit) => hit.date)).size,
      onShowConflicts: () => multi.onShowConflicts(name, hits),
    };
  };

  const users = useMemo(
    () =>
      [...crewOptions, ...fieldUserOptions].filter(
        (el) => el.id !== 'unassigned' && el.id !== schedule.foreman?.id && !el.discardedAt,
      ),
    [crewOptions, fieldUserOptions, schedule.foreman?.id],
  );
  const options = sortByName(
    users.map((el) => ({
      value: el.id,
      label: el.fullName || '',
      sublabel: el.primaryRole ? tPrimaryRoles(snakeToCamelCase(String(el.primaryRole))) : '',
      conflicts: multi ? [] : conflictsByUserId[el.id] || [],
      qualified: !specialty || Boolean(el.specialtyValues?.includes(specialty)),
      ...multiConflicts(el.id, el.fullName || ''),
    })),
  );
  const selected = crewMembers.filter((el) => el.userId && !el.discardedAt).map((el) => el.userId);

  const toMember = (userId: string): SchedulerSidebarCrewMember => {
    const user = users.find((el) => el.id === userId);
    return {
      id: undefined,
      userId,
      name: user?.fullName || '',
      primaryRole: user?.primaryRole || undefined,
      conflicts: [],
      roles: user?.roles.map((el) => ({ ...el, id: '' })) || [],
      edit: false,
    };
  };

  const toggle = (userId: string) =>
    setState((prev) => ({
      ...prev,
      defaultCrew: false,
      crewMembers: selected.includes(userId)
        ? prev.crewMembers.filter((el) => el.userId !== userId)
        : [...prev.crewMembers, toMember(userId)],
    }));

  const replace = (fromId: string, toId: string) => {
    if (toId === fromId || selected.includes(toId)) return;
    setState((prev) => ({
      ...prev,
      defaultCrew: false,
      crewMembers: prev.crewMembers.map((el) => (el.userId === fromId ? toMember(toId) : el)),
    }));
  };

  return {
    options,
    selected,
    toggle,
    replace,
    multiConflicts,
    disabled: crewDisabled || fieldUsersDisabled,
  };
};
